import { XIcon } from "./product-icons.tsx";
import { closeTileClassName } from "./close-tile.ts";
import { edgePanelRef, edgeScrimRef } from "./browser-edges.tsx";
import { EDGE_COLORS } from "./edge-color.ts";
import { DIALOG_SCRIM, dialogOverlayClassName } from "./overlay-motion.ts";

import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "../../lib/utils.ts";
import type * as React from "react";

function Dialog({
    ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
    return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger({
    ...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
    return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({
    ...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
    return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({
    className,
    variant = "default",
    ...props
}: React.ComponentProps<typeof DialogPrimitive.Close> & {
    variant?: "default" | "tile";
}) {
    return (
        <DialogPrimitive.Close
            data-slot="dialog-close"
            className={
                variant === "tile"
                    ? cn(closeTileClassName, className)
                    : className
            }
            {...props}
        />
    );
}

function DialogOverlay({
    className,
    ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
    return (
        <DialogPrimitive.Overlay
            data-slot="dialog-overlay"
            className={cn(dialogOverlayClassName, className)}
            {...props}
        />
    );
}

function DialogContent({
    className,
    children,
    showCloseButton = true,
    fullScreen = false,
    fullScreenTransition = "slide",
    surface = "default",
    ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
    showCloseButton?: boolean;
    surface?: "default" | "maltese";

    fullScreen?: boolean;

    fullScreenTransition?: "slide" | "fade";
}) {
    return (
        <DialogPortal data-slot="dialog-portal">
            <DialogOverlay
                ref={edgeScrimRef(fullScreen ? null : DIALOG_SCRIM.tint)}
                className={cn(
                    fullScreen && "bg-transparent backdrop-blur-none",
                )}
            />
            <DialogPrimitive.Content
                data-slot="dialog-content"
                ref={edgePanelRef(
                    fullScreen
                        ? surface === "maltese"
                            ? EDGE_COLORS.malteseWhite
                            : EDGE_COLORS.trueWhite
                        : null,
                )}
                className={cn(
                    fullScreen
                        ? cn(
                              "bg-true-white fixed inset-0 z-50 flex w-full max-w-none flex-col gap-0 overflow-hidden rounded-none border-0 p-0 shadow-none outline-none sm:max-w-none data-[state=open]:animate-in data-[state=closed]:animate-out",
                              fullScreenTransition === "fade"
                                  ? "will-change-[opacity] data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:duration-150 data-[state=open]:duration-200 data-[state=closed]:ease-out data-[state=open]:ease-out"
                                  : "will-change-transform data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom data-[state=closed]:duration-[260ms] data-[state=open]:duration-[400ms] data-[state=closed]:ease-sheet-rise data-[state=open]:ease-sheet-rise motion-reduce:data-[state=closed]:slide-out-to-bottom-0 motion-reduce:data-[state=open]:slide-in-from-bottom-0",
                          )
                        : "fixed top-1/2 left-1/2 z-50 grid max-h-[calc(100dvh-2rem)] w-full max-w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 gap-4 overflow-y-auto rounded-surface border-0 bg-true-white p-6 shadow-[0_1px_2px_rgba(49,36,28,0.06),0_24px_72px_-24px_rgba(49,36,28,0.32)] ring-1 ring-doberman-black/[0.07] outline-none will-change-[transform,opacity] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-[0.98] data-[state=open]:zoom-in-[0.98] data-[state=closed]:duration-150 data-[state=open]:duration-[220ms] data-[state=closed]:ease-out-quint data-[state=open]:ease-out-quint motion-reduce:animate-none sm:max-w-lg",
                    surface === "maltese" && "bg-maltese-white",
                    className,
                )}
                {...props}
            >
                {showCloseButton && (
                    <DialogPrimitive.Close
                        data-slot="dialog-close"
                        className={cn(
                            closeTileClassName,
                            "sticky top-4 z-10 -mb-11 ml-auto disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0",
                        )}
                    >
                        <XIcon className="size-5" />
                        <span className="sr-only">Close</span>
                    </DialogPrimitive.Close>
                )}
                {children}
            </DialogPrimitive.Content>
        </DialogPortal>
    );
}

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
    return (
        <div
            data-slot="dialog-header"
            className={cn(
                "flex flex-col gap-2 text-center sm:text-left",
                className,
            )}
            {...props}
        />
    );
}

function DialogFooter({ className, ...props }: React.ComponentProps<"div">) {
    return (
        <div
            data-slot="dialog-footer"
            className={cn(
                "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
                className,
            )}
            {...props}
        />
    );
}

function DialogTitle({
    className,
    variant = "default",
    ...props
}: React.ComponentProps<typeof DialogPrimitive.Title> & {
    variant?: "default" | "h2";
}) {
    return (
        <DialogPrimitive.Title
            data-slot="dialog-title"
            className={cn(
                "font-family-heading text-[1.375rem] leading-[1.15] font-semibold tracking-[-0.02em]",
                variant === "h2" && "text-h2 text-doberman-black",
                className,
            )}
            {...props}
        />
    );
}

const dialogCaptionClassName =
    "text-body-small font-medium tabular-nums text-doberman-black/50";

function DialogDescription({
    className,
    variant = "default",
    ...props
}: React.ComponentProps<typeof DialogPrimitive.Description> & {
    variant?: "default" | "caption";
}) {
    return (
        <DialogPrimitive.Description
            data-slot="dialog-description"
            className={cn(
                variant === "caption"
                    ? dialogCaptionClassName
                    : "text-muted-foreground text-sm leading-relaxed",
                className,
            )}
            {...props}
        />
    );
}

function DialogHeaderRow({
    title,
    description,
    children,
    className,
}: {
    title: React.ReactNode;
    description?: React.ReactNode;
    children?: React.ReactNode;
    className?: string;
}) {
    return (
        <div
            data-slot="dialog-header-row"
            className={cn("flex shrink-0 items-center gap-6", className)}
        >
            <div className="min-w-0">
                <DialogTitle>{title}</DialogTitle>
                {description && (
                    <DialogDescription variant="caption">
                        {description}
                    </DialogDescription>
                )}
            </div>
            <div className="flex min-w-0 flex-1 items-center justify-end gap-3">
                {children}
                <DialogClose variant="tile">
                    <XIcon className="size-5" />
                    <span className="sr-only">Close</span>
                </DialogClose>
            </div>
        </div>
    );
}

export {
    dialogCaptionClassName,
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogHeaderRow,
    DialogOverlay,
    DialogPortal,
    DialogTitle,
    DialogTrigger,
};
